<template lang="pug">
  div.yz-dropdown(:class="sizeClass" :style="widthStyle")
    yz-menu(v-model="menuOpen" :offset-y="true" :disabled="disabled || readonly" :close-on-content-click="false" content-class="yz-dropdown-menu-content" :attach="attach || undefined")
      template(v-slot:activator="{ on, attrs }")
        div.yz-dropdown-activator(v-bind="attrs" v-on="on" tabindex="0" @blur="onBlur")
          yz-tooltip(:disabled="!shouldShowTooltip" direction="bottom")
            div(v-if="chips && hasValue" :class="['yz-dropdown-chips-container', sizeClass, {'yz-dropdown-chips-container--error': hasError}]")
              .yz-dropdown-chips-field(:class="fontClass")
                // Single selection chip
                yz-badge(v-if="!multiple && value != null" size="sm" variant="primary" closable @close="clearValue") {{ getChipText() }}
                // Multiple selection chips
                template(v-else-if="multiple && selectedItems.length > 0")
                  yz-badge(v-for="(item, index) in selectedItems" :key="index" size="sm" variant="primary" :closable="!disabled && !readonly" @close="removeItem(item)") {{ getItemText(item) }}
                // Placeholder when no chips
                span.yz-dropdown-placeholder(v-if="!hasValue" :class="fontClass") {{ placeholder }}
              yz-icon(size="18" style="margin-right: 2px" @click="onAppendClick") {{ computedAppendIcon }}
            yz-text-field(v-else v-bind="textFieldAttrs" :value="displayText" :size="size" readonly :disabled="disabled" :append-icon="computedAppendIcon" :class="fontClass" @click:append="onAppendClick")
            template(v-slot:tooltip)
              div.yz-tooltip-content
                div(v-for="(item, index) in selectedItems" :key="index") {{ getItemText(item) }}
      yz-list
        div(v-if="!multiple")
          yz-list-item(v-for="(item, index) in items" :key="index" @click="selectItem(item)" :class="{ 'v-list-item--active': isSelected(item) }")
            slot(name="item" :item="item" :index="index")
              div {{ getItemText(item) }}
        div(v-else)
          yz-list-item(v-for="(item, index) in items" :key="index" @click="toggleItem(item)")
            yz-checkbox(:value="isSelected(item)" @input="toggleItem(item)" :label="getItemText(item)")
              template(v-slot:label)
                slot(name="item" :item="item" :index="index")
                  div {{ getItemText(item) }}
    .yz-regular-body-xs.ml-3.error--text.mt-2.mb-3(v-if="computedErrorMessage") {{ computedErrorMessage }}

</template>

<script>
export default {
  name: 'YzDropdown',
  props: {
    value: {
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    itemText: {
      type: [String, Function],
      default: 'text'
    },
    itemValue: {
      type: [String, Function],
      default: 'value'
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'lg',
      validator: (value) => ['lg', 'md', 'sm'].includes(value)
    },
    appendIcon: {
      type: String,
      default: '$yzi-chevron-down'
    },
    fontClass: {
      type: String,
      default: 'yz-regular-body-sm'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: [Array, String],
      default: () => []
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    chips: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: 'auto'
    },
    attach: {
      type: [String, Boolean, Object],
      default: undefined
    },
    // Add rules prop for more flexible validation
    rules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      menuOpen: false,
      hasBeenValidated: false,
      internalErrorMessages: []
    }
  },
  computed: {
    modelValue: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    textFieldAttrs() {
      // eslint-disable-next-line no-unused-vars
      const {items, itemText, itemValue, multiple, appendIcon, rules, ...attrs} = this.$attrs

      // Only show placeholder when there's no value
      const shouldShowPlaceholder = this.multiple
          ? (!Array.isArray(this.value) || this.value.length === 0)
          : this.value == null || this.value === ''

      return {
        ...attrs,
        placeholder: shouldShowPlaceholder ? this.placeholder : undefined,
        error: this.hasError,
        errorMessages: this.errorMessages
      }
    },
    sizeClass() {
      return [
        `yz-dropdown--${this.size}`,
        {'yz-dropdown--disabled': this.disabled},
        {'yz-dropdown--readonly': this.readonly},
        {'yz-dropdown--menu-open': this.menuOpen}
      ]
    },
    selectedItems() {
      if (!this.multiple) return []
      if (!Array.isArray(this.value)) return []

      if (this.returnObject) {
        return this.value
      } else {
        return this.items.filter(item => this.value.includes(this.getItemValue(item)))
      }
    },
    displayText() {
      if (this.multiple) {
        // For multiple selection, show the comma-separated text directly
        if (!Array.isArray(this.value) || this.value.length === 0) {
          return null
        }
        return this.getSelectionText()
      } else {
        if (this.value === null || this.value === undefined || this.value === '') {
          return null
        }

        if (this.returnObject) {
          return this.getItemText(this.value)
        } else {
          const selectedItem = this.items.find(item => this.getItemValue(item) === this.value)
          return selectedItem ? this.getItemText(selectedItem) : this.placeholder
        }
      }
    },
    shouldShowTooltip() {
      // Always show tooltip for multiple selections with more than 1 item, 
      // so users can see all selected items clearly
      return this.multiple && this.selectedItems.length > 1
    },
    computedAppendIcon() {
      // Show clear icon when clearable is true and there's a value, but not when disabled or readonly
      if (this.clearable && this.hasValue && !this.disabled && !this.readonly) {
        return 'mdi-close'
      }
      return this.appendIcon
    },
    hasValue() {
      if (this.multiple) {
        return Array.isArray(this.value) && this.value.length > 0
      }
      return this.value !== null && this.value !== ''
    },
    widthStyle() {
      const style = {}

      if (this.width && this.width !== 'auto') {
        let widthValue

        // Handle predefined sizes
        if (this.width === 'full') {
          widthValue = '100%'
        } else if (this.width === 'fit-content') {
          widthValue = 'fit-content'
        } else {
          // Use the width value as-is for CSS units (px, %, rem, em, vw, vh, etc.)
          widthValue = this.width
        }

        style.width = widthValue
      }

      return style
    },
    // Merge required validation into rules array
    mergedRules() {
      const rules = [...this.rules];

      // If required is true, prepend the required validation rule
      if (this.required) {
        rules.unshift(v => {
          if (this.multiple) {
            return (Array.isArray(v) && v.length > 0) || this.requiredErrorMessage;
          } else {
            return (v !== null && v !== undefined && v !== '') || this.requiredErrorMessage;
          }
        });
      }

      return rules;
    },
    computedErrorMessage() {
      // Simplified - only check merged rules validation results
      if (this.internalErrorMessages.length > 0) {
        return this.hasBeenValidated ? this.internalErrorMessages[0] : '';
      }
      return '';
    },
    hasError() {
      return !!this.computedErrorMessage;
    }
  },
  watch: {
    menuOpen(isOpen) {
      if (!isOpen) {
        // Validate when menu closes (user clicked outside or selected item)
        this.validate(true)
      }
    },
    value: {
      handler() {
        // Only validate if already been validated (for reactive validation)
        if (this.hasBeenValidated) {
          this.validate();
        }
      },
      immediate: false
    },
    mergedRules: {
      handler() {
        this.validate();
      },
      immediate: false,
      deep: true
    }
  },
  mounted() {
    // Register with parent v-form if it exists
    this.form = this.getForm();
    if (this.form && this.form.register) {
      this.form.register(this);
    }
  },
  beforeDestroy() {
    // Unregister from parent v-form
    if (this.form && this.form.unregister) {
      this.form.unregister(this);
    }
  },
  methods: {
    getItemText(item) {
      if (typeof this.itemText === 'function') {
        return this.itemText(item)
      }
      if (typeof item === 'object' && item !== null) {
        return item[this.itemText] || item.text || item.label || String(item)
      }
      return String(item)
    },
    getItemValue(item) {
      if (typeof this.itemValue === 'function') {
        return this.itemValue(item)
      }
      if (typeof item === 'object' && item !== null) {
        return item[this.itemValue] !== undefined ? item[this.itemValue] : item.value !== undefined ? item.value : item
      }
      return item
    },
    getChipText() {
      if (this.value == null) return ''

      if (this.returnObject) {
        return this.getItemText(this.value)
      } else {
        const selectedItem = this.items.find(item => this.getItemValue(item) === this.value)
        return selectedItem ? this.getItemText(selectedItem) : ''
      }
    },
    isSelected(item) {
      const itemValue = this.getItemValue(item)
      if (this.multiple) {
        if (!Array.isArray(this.value)) return false

        if (this.returnObject) {
          return this.value.some(val => this.getItemValue(val) === itemValue)
        } else {
          return this.value.includes(itemValue)
        }
      }

      if (this.returnObject) {
        return this.value && this.getItemValue(this.value) === itemValue
      } else {
        return this.value === itemValue
      }
    },
    selectItem(item) {
      if (this.disabled || this.readonly) return
      const returnValue = this.returnObject ? item : this.getItemValue(item)
      this.modelValue = returnValue
      this.menuOpen = false
      this.$emit('change', returnValue)
      this.validate(true)
    },
    toggleItem(item) {
      if (this.disabled || this.readonly) return
      if (!this.multiple) return

      const returnValue = this.returnObject ? item : this.getItemValue(item)
      const compareValue = this.getItemValue(item)
      let newValue = Array.isArray(this.value) ? [...this.value] : []

      // For comparison, we need to check against the appropriate value based on returnObject
      const existingIndex = this.returnObject
          ? newValue.findIndex(val => this.getItemValue(val) === compareValue)
          : newValue.indexOf(compareValue)

      if (existingIndex > -1) {
        newValue.splice(existingIndex, 1)
      } else {
        newValue.push(returnValue)
      }

      this.modelValue = newValue
      this.$emit('change', newValue)
      this.validate(true)
    },
    removeItem(item) {
      if (this.disabled || this.readonly) return
      if (!this.multiple) return

      const compareValue = this.getItemValue(item)
      let newValue = Array.isArray(this.value) ? [...this.value] : []

      // For comparison, we need to check against the appropriate value based on returnObject
      const index = this.returnObject
          ? newValue.findIndex(val => this.getItemValue(val) === compareValue)
          : newValue.indexOf(compareValue)

      if (index > -1) {
        newValue.splice(index, 1)
        this.modelValue = newValue
        this.$emit('change', newValue)
        this.validate(true)
      }
    },
    getSelectionText() {
      if (!this.multiple || !this.selectedItems.length) return ''
      return this.selectedItems.map(item => this.getItemText(item)).join(', ')
    },
    onAppendClick(event) {
      if (this.disabled || this.readonly) return

      if (this.chips && this.hasValue && !this.clearable) {
        // In chips mode, handle clear functionality with individual chip close buttons
        // For the main chevron, let the click bubble up to open the menu
        return
      }
      // In non-chips mode, handle clear functionality when clear icon is clicked
      if (this.clearable && this.hasValue) {
        event.stopPropagation() // Prevent menu from opening
        this.clearValue()
      } else {
        // Toggle dropdown menu when chevron is clicked
        this.menuOpen = !this.menuOpen
      }
    },
    clearValue() {
      if (this.disabled || this.readonly) return

      const newValue = this.multiple ? [] : null
      this.modelValue = newValue
      this.$emit('change', newValue)
      this.$emit('clear')
    },
    onBlur() {
      // Only validate if menu is already closed (prevents validation when opening menu)
      if (!this.menuOpen) {
        this.validate(true)
      }
    },
    // Main validation method that v-form will call
    validate(force = false) {
      if (force) {
        this.hasBeenValidated = true;
      }

      // If not yet validated and not forced, don't show errors
      if (!this.hasBeenValidated && !force) {
        return true;
      }

      this.hasBeenValidated = true;
      this.internalErrorMessages = [];

      // Run merged rules (includes required validation if set)
      if (this.mergedRules.length > 0) {
        for (const rule of this.mergedRules) {
          const result = typeof rule === 'function' ? rule(this.value) : rule;
          if (result !== true) {
            this.internalErrorMessages.push(result || 'Invalid value');
          }
        }

        if (this.internalErrorMessages.length > 0) {
          return false;
        }
      }

      return true;
    },

    // Just clear validation errors without resetting value
    resetValidation() {
      this.hasBeenValidated = false;
      this.internalErrorMessages = [];
    },

    // Helper method to find parent v-form
    getForm() {
      let parent = this.$parent;

      while (parent) {
        // Check if it's a v-form by name or by checking for form methods
        if (parent.$options.name === 'v-form' || (parent.register && parent.unregister && parent.validate)) {
          return parent;
        }

        // Also check if parent has a form ref (for custom form wrappers)
        if (parent.$refs && parent.$refs.form &&
            parent.$refs.form.register && parent.$refs.form.unregister) {
          return parent.$refs.form;
        }

        parent = parent.$parent;
      }

      return null;
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select.v-text-field--outlined {
    fieldset {
      border-color: var(--yz-neutral-400);
    }
  }

  // Apply font class to selection text
  .v-select__selection {
    font-size: inherit;
  }

  // Size-specific styles for v-select
  &.yz-dropdown--lg {
    .v-input__slot {
      min-height: 36px !important;
      height: 36px !important;
    }

    .v-select__slot {
      height: 36px !important;
    }

    .v-select__selection {
      line-height: 36px !important;
      max-height: 36px !important;
    }
  }

  &.yz-dropdown--md {
    .v-input__slot {
      min-height: 32px !important;
      height: 32px !important;
    }

    .v-select__slot {
      height: 32px !important;
    }

    .v-select__selection {
      line-height: 32px !important;
      max-height: 32px !important;
    }
  }

  &.yz-dropdown--sm {
    .v-input__slot {
      min-height: 28px !important;
      height: 28px !important;
    }

    .v-select__slot {
      height: 28px !important;
    }

    .v-select__selection {
      line-height: 28px !important;
      max-height: 28px !important;
    }
  }

  .error--text {
    color: var(--yz-danger) !important;

    fieldset {
      border-color: var(--yz-danger);
    }
  }

  // Icon positioning for lg size (36px height)
  &.yz-dropdown--lg {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 9px !important;
      margin-bottom: 9px !important;
      height: 18px !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }

    // Specific positioning for dropdown chevron icon
    .v-select__slot .v-input__append-inner {
      margin-top: 9px !important;
      align-self: center !important;

      .v-input__icon {
        margin-top: 0 !important;

        .v-icon {
          height: 18px !important;
          width: 18px !important;
          font-size: 18px !important;
        }
      }
    }
  }

  // Icon positioning for md size (32px height)
  &.yz-dropdown--md {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 7px !important;
      margin-bottom: 7px !important;
      height: 18px !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }

    // Specific positioning for dropdown chevron icon
    .v-select__slot .v-input__append-inner {
      margin-top: 7px !important;
      align-self: center !important;

      .v-input__icon {
        margin-top: 0 !important;

        .v-icon {
          height: 18px !important;
          width: 18px !important;
          font-size: 18px !important;
        }
      }
    }
  }

  // Icon positioning for sm size (28px height)
  &.yz-dropdown--sm {
    .v-input__prepend-outer,
    .v-input__append-outer,
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 5px !important;
      margin-bottom: 5px !important;
      height: 18px !important;

      .v-input__icon,
      .v-icon {
        height: 18px !important;
        width: 18px !important;
        font-size: 18px !important;
      }

      .v-icon__component {
        height: 18px !important;
        width: 18px !important;
      }
    }

    // Specific positioning for dropdown chevron icon
    .v-select__slot .v-input__append-inner {
      margin-top: 5px !important;
      align-self: center !important;

      .v-input__icon {
        margin-top: 0 !important;

        .v-icon {
          height: 18px !important;
          width: 18px !important;
          font-size: 18px !important;
        }
      }
    }
  }

  // Dropdown arrow positioning and rotation
  .v-text-field .v-input__append-inner {
    .v-input__icon {
      .v-icon {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
    }
  }

  // Rotate icon when menu is open
  &.yz-dropdown--menu-open {
    .v-text-field .v-input__append-inner {
      .v-input__icon {
        .v-icon {
          transform: rotate(180deg);
        }
      }
    }
  }

  // Ensure consistent styling with YzTextField
  .v-text-field--outlined .v-input__slot {
    border-radius: 10px;
  }

  .v-text-field--outlined fieldset {
    border-radius: 10px;
  }

  // Activator wrapper styling
  .yz-dropdown-activator {
    position: relative;
    display: block;
    cursor: pointer;
  }


  // Ensure text truncation with ellipsis
  .v-text-field input {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Override cursor for dropdown text field
  &:not(.yz-dropdown--disabled) {
    input[readonly] {
      cursor: pointer !important;
    }

    .v-input__slot {
      cursor: pointer !important;
    }

    // Ensure icon area is clickable
    .v-input__append-inner {
      cursor: pointer !important;
      pointer-events: auto !important;

      .v-input__icon {
        cursor: pointer !important;
        pointer-events: auto !important;
      }
    }
  }

  // Disabled state cursor
  &.yz-dropdown--disabled {
    .yz-dropdown-activator {
      cursor: not-allowed;
    }

    input[readonly] {
      cursor: not-allowed !important;
    }

    .v-input__slot {
      cursor: not-allowed !important;
    }
  }

  // Readonly state cursor - same as disabled but maintains normal appearance
  &.yz-dropdown--readonly {
    .yz-dropdown-activator {
      cursor: not-allowed;
    }

    input[readonly] {
      cursor: not-allowed !important;
    }

    .v-input__slot {
      cursor: not-allowed !important;
    }

    // Ensure icon area shows not-allowed cursor
    .v-input__append-inner {
      cursor: not-allowed !important;
      pointer-events: auto !important;

      .v-input__icon {
        cursor: not-allowed !important;
        pointer-events: auto !important;
      }
    }
  }
}

::v-deep .yz-dropdown-menu-content {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;

  .v-select-list {
    border-radius: 8px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;

    & .v-list-item > .v-list-item__content > .v-list-item__title {
      font-size: var(--yz-regular-body-sm) !important;
    }

    .v-list {
      border-radius: 8px !important;

      // Match YzList font class

      .v-list-item {
        border-radius: 6px !important;
        margin: 2px 8px !important;
        min-height: 36px !important;
        padding: 0 12px !important;

        &:first-child {
          margin-top: 8px !important;
        }

        &:last-child {
          margin-bottom: 8px !important;
        }

        &:hover {
          background-color: var(--yz-neutral-100) !important;
        }

        &.v-list-item--active {
          background-color: var(--yz-primary-100) !important;

          .v-list-item__content,
          .v-list-item__title {
            color: var(--yz-primary-700) !important;
          }
        }

        .v-list-item__content {
          padding: 0 !important;
        }
      }
    }
  }
}

// Additional font classes support
.yz-dropdown-menu-content .v-list {
  &.yz-regular-body-xs .v-list-item__content,
  &.yz-regular-body-xs .v-list-item__title {
    font-size: var(--yz-regular-body-xs) !important;
  }

  &.yz-regular-body-md .v-list-item__content,
  &.yz-regular-body-md .v-list-item__title {
    font-size: var(--yz-regular-body-md) !important;
  }

  &.yz-regular-body-lg .v-list-item__content,
  &.yz-regular-body-lg .v-list-item__title {
    font-size: var(--yz-regular-body-lg) !important;
  }
}

// Chips container styles
.yz-dropdown-chips-container {
  border: 1px solid var(--yz-neutral-400);
  border-radius: 10px;
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--yz-neutral-500);
  }

  &:focus-within {
    border-color: var(--yz-primary);
  }

  &.yz-dropdown--disabled {
    background-color: var(--yz-neutral-100);
    cursor: not-allowed;
    border-color: var(--yz-neutral-400);
  }

  // Readonly state for chips container - maintains normal appearance but not-allowed cursor
  &.yz-dropdown--readonly {
    cursor: not-allowed;

    &:hover {
      border-color: var(--yz-neutral-400);
    }
  }

  // Size-specific heights
  &.yz-dropdown--lg {
    min-height: 36px;
  }

  &.yz-dropdown--md {
    min-height: 32px;
  }

  &.yz-dropdown--sm {
    min-height: 28px;
  }

  // Error state styling
  &.yz-dropdown-chips-container--error {
    border-color: var(--yz-danger) !important;
    background-color: var(--yz-danger-50) !important;

    &:hover {
      border-color: var(--yz-danger) !important;
      background-color: var(--yz-danger-100) !important;
    }

    &:focus-within {
      border-color: var(--yz-danger) !important;
      background-color: var(--yz-danger-100) !important;
    }
  }
}

.yz-dropdown-chips-field {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 20px;
}

.yz-dropdown-placeholder {
  color: var(--yz-neutral-500);
  font-style: italic;
}

// Chevron rotation for chips mode
.yz-dropdown-chips-container .v-icon {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// Rotate chevron in chips mode when menu is open
.yz-dropdown.yz-dropdown--menu-open .yz-dropdown-chips-container .v-icon {
  transform: rotate(180deg);
}
</style>